import { useContext, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Layout, Menu, Tooltip, Button } from "antd";
import type { MenuProps } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { ThemeContext } from "../contexts/ThemeContext";

const { Sider } = Layout;

const baseSiderStyles: CSSProperties = {
	height: "100vh",
	position: "fixed",
	left: 0,
	top: 0,
	bottom: 0,
	overflow: "auto",
	zIndex: 10,
};

type SidebarSlot = ReactNode | ((collapsed: boolean) => ReactNode);

export interface FileHelixSidebarProps {
	title?: ReactNode;
	collapsedTitle?: ReactNode;
	collapsed?: boolean;
	defaultCollapsed?: boolean;
	onCollapse?: (collapsed: boolean) => void;
	width?: number;
	collapsedWidth?: number;
	menuItems?: MenuProps["items"];
	selectedKeys?: string[];
	openKeys?: string[];
	menuProps?: Omit<MenuProps, "items" | "selectedKeys" | "openKeys">;
	topSlot?: SidebarSlot;
	footerSlot?: SidebarSlot;
	showCollapseToggle?: boolean;
	collapseTooltip?: string;
	expandTooltip?: string;
	collapseButtonLabel?: string;
	expandButtonLabel?: string;
	theme?: "light" | "dark";
	collapsible?: boolean;
	style?: CSSProperties;
	className?: string;
}

export type { SidebarSlot };

const renderSlot = (slot: SidebarSlot | undefined, collapsed: boolean) => {
	if (!slot) return null;
	return typeof slot === "function" ? slot(collapsed) : slot;
};

export function FileHelixSidebar({
	title = "FileHelix",
	collapsedTitle = "FH",
	collapsed,
	defaultCollapsed = false,
	onCollapse,
	width = 240,
	collapsedWidth = 80,
	menuItems,
	selectedKeys,
	openKeys,
	menuProps,
	topSlot,
	footerSlot,
	showCollapseToggle = false,
	collapseTooltip = "Collapse sidebar",
	expandTooltip = "Expand sidebar",
	collapseButtonLabel = "Collapse sidebar",
	expandButtonLabel = "Expand sidebar",
	theme,
	collapsible = false,
	style,
	className,
}: FileHelixSidebarProps) {
	const themeContext = useContext(ThemeContext);
	const resolvedTheme = theme ?? (themeContext?.effectiveTheme === "dark" ? "dark" : "light");
	const isDark = resolvedTheme === "dark";
	const dividerColor = isDark ? "rgba(255,255,255,0.12)" : "#f0f0f0";
	const sidebarBg = isDark ? "#141414" : "#fff";
	const surfaceBg = isDark ? "rgba(255,255,255,0.03)" : "#fff";
	const textColor = isDark ? "rgba(255,255,255,0.9)" : undefined;

	const isControlled = typeof collapsed === "boolean";
	const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
	const effectiveCollapsed = collapsible ? (isControlled ? (collapsed as boolean) : internalCollapsed) : false;

	const handleCollapse = (value: boolean) => {
		if (!collapsible) return;
		if (!isControlled) {
			setInternalCollapsed(value);
		}
		onCollapse?.(value);
	};

	const collapseToggleEnabled = collapsible && showCollapseToggle;
	const hasTitle = title !== null && title !== undefined;
	const resolvedTitle = effectiveCollapsed ? (collapsedTitle ?? title) : title;

	const {
		style: menuStyle,
		className: menuClassName,
		mode: menuModeProp,
		_internalDisableMenuItemTitleTooltip,
		...restMenuProps
	} = menuProps || {};
	const menuMode = menuModeProp ?? "inline";
	const disableMenuItemTitleTooltip = _internalDisableMenuItemTitleTooltip ?? true;

	const mergedSiderStyles = useMemo(
		() => ({
			...baseSiderStyles,
			background: sidebarBg,
			boxShadow: isDark ? "2px 0 12px rgba(0,0,0,0.65)" : "2px 0 8px rgba(0,0,0,0.05)",
			borderRight: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined,
			color: textColor,
			...(style || {}),
		}),
		[sidebarBg, isDark, style, textColor],
	);

	return (
		<Sider
			collapsible={collapsible}
			collapsed={effectiveCollapsed}
			collapsedWidth={collapsedWidth}
			onCollapse={handleCollapse}
			width={width}
			theme={resolvedTheme}
			style={mergedSiderStyles}
			trigger={null}
			className={["v2-file-helix-sidebar", className].filter(Boolean).join(" ")}
		>
			<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
				{hasTitle && (
					<div
						className="v2-file-helix-sidebar__title"
						style={{
							height: 64,
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-start",
							fontSize: effectiveCollapsed ? 16 : 20,
							fontWeight: 600,
							color: textColor || "#111827",
							borderBottom: `1px solid ${dividerColor}`,
							padding: "0 16px",
							transition: "font-size 0.2s",
							whiteSpace: "nowrap",
							textAlign: "left",
							gap: 8,
						}}
					>
						{resolvedTitle}
					</div>
				)}

				{topSlot && (
					<div
						style={{
							padding: 16,
							borderBottom: `1px solid ${dividerColor}`,
							background: surfaceBg,
						}}
					>
						{renderSlot(topSlot, effectiveCollapsed)}
					</div>
				)}

				{menuItems ? (
					<div style={{ flex: 1, overflowY: "auto" }}>
						<Menu
							items={menuItems}
							selectedKeys={selectedKeys ?? []}
							{...(openKeys ? { openKeys } : {})}
							mode={menuMode}
							_internalDisableMenuItemTitleTooltip={disableMenuItemTitleTooltip}
							{...restMenuProps}
							className={["v2-file-helix-menu", menuClassName].filter(Boolean).join(" ")}
							style={{
								border: "none",
								padding: "8px",
								background: sidebarBg,
								color: textColor,
								...(menuStyle || {}),
							}}
						/>
					</div>
				) : (
					<div style={{ flex: 1 }} />
				)}

				{(footerSlot || collapseToggleEnabled) && (
					<div
						style={{
							padding: 16,
							borderTop: `1px solid ${dividerColor}`,
							background: surfaceBg,
						}}
					>
						{renderSlot(footerSlot, effectiveCollapsed)}
						{collapseToggleEnabled && (
							<Tooltip
								title={effectiveCollapsed ? expandTooltip : collapseTooltip}
								placement={effectiveCollapsed ? "right" : "top"}
							>
								<Button
									type="text"
									block
									icon={effectiveCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
									onClick={() => handleCollapse(!effectiveCollapsed)}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: effectiveCollapsed ? "center" : "flex-start",
										marginTop: footerSlot ? 8 : 0,
									}}
								>
									{!effectiveCollapsed && (effectiveCollapsed ? expandButtonLabel : collapseButtonLabel)}
								</Button>
							</Tooltip>
						)}
					</div>
				)}
			</div>
		</Sider>
	);
}
